/**
 * Module-level subject tracking how many SplitPanels are currently open.
 *
 * Used so floating UI (AstorWidget bubble, future overlays) can react when a
 * SplitPanel takes over the right half of the screen and would otherwise
 * collide with the panel's footer buttons. Decoupled from SplitPanel by
 * design — the panel registers itself, consumers subscribe via
 * `useAnySplitPanelOpen()`, and neither knows about the other.
 *
 * Multi-panel safe: each open panel registers a unique symbol so two panels
 * can be open at once (rare in practice, but the page may render a second
 * SplitPanel for a sub-flow) without races on the count.
 */

import { useEffect, useState } from 'react';

const openPanels = new Set<symbol>();
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

/**
 * Mark one SplitPanel as open. Returns a cleanup function the caller MUST
 * invoke when the panel closes or unmounts.
 */
export function registerOpenSplitPanel(): () => void {
  const id = Symbol('split-panel');
  openPanels.add(id);
  notify();
  return () => {
    openPanels.delete(id);
    notify();
  };
}

/**
 * Reactive boolean: true when any SplitPanel anywhere in the tree is open.
 *
 * Consumers re-render on every open/close transition. Subscribes once per
 * mount; the listener is cleaned up on unmount so leaving a page that uses
 * this hook never leaves a dangling subscription.
 */
export function useAnySplitPanelOpen(): boolean {
  const [open, setOpen] = useState(openPanels.size > 0);
  useEffect(() => {
    const update = (): void => setOpen(openPanels.size > 0);
    listeners.add(update);
    update();
    return () => {
      listeners.delete(update);
    };
  }, []);
  return open;
}
